import { useState } from "react";
import axios from "axios";
import PublicLayout from "../../components/marketing/PublicLayout";
import { useMarketingLang } from "../../lib/useMarketingLang";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MarketingContact() {
  const { t } = useMarketingLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState(null); // "ok" | "err" | null
  const [busy, setBusy] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      await axios.post(`${API}/public/contact`, form);
      setStatus("ok");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      setStatus("err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <section className="bg-[#0F1A2B] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.42em] text-[#ECC974] mb-3">{t.contact.eyebrow}</div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-3">{t.contact.title}</h1>
          <p className="text-white/75">{t.contact.sub}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-[#A57F39]/20 shadow-sm p-6 lg:p-8">
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t.contact.form.name} required value={form.name} onChange={change("name")} testid="contact-name" />
              <Field label={t.contact.form.email} type="email" required value={form.email} onChange={change("email")} testid="contact-email" />
              <Field label={t.contact.form.phone} value={form.phone} onChange={change("phone")} testid="contact-phone" />
              <Field label={t.contact.form.subject} value={form.subject} onChange={change("subject")} testid="contact-subject" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">
                {t.contact.form.message}
              </label>
              <textarea
                data-testid="contact-message"
                required
                rows={5}
                value={form.message}
                onChange={change("message")}
                className="w-full rounded-lg border border-[#A57F39]/25 focus:border-[#A57F39] focus:outline-none px-3 py-2.5 text-[#0F1A2B]"
              />
            </div>
            {status === "ok" && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 text-sm" data-testid="contact-sent">
                {t.contact.form.sent}
              </div>
            )}
            {status === "err" && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" data-testid="contact-err">
                {t.contact.form.error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#A57F39] hover:bg-[#BD984D] disabled:opacity-60 transition text-white uppercase tracking-widest text-xs font-bold px-6 py-3"
            >
              <Send className="h-4 w-4" /> {busy ? "…" : t.contact.form.send}
            </button>
          </form>
        </div>

        <aside className="rounded-2xl bg-[#F8F7F2] border border-[#A57F39]/20 p-6 lg:p-8 self-start">
          <h3 className="text-lg font-bold text-[#0F1A2B] mb-5">{t.contact.infoTitle}</h3>
          <ul className="space-y-4 text-sm text-[#0F1A2B]/85">
            <li className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-[#A57F39] mt-0.5 shrink-0" />
              <span>{t.footer.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-[#A57F39] mt-0.5 shrink-0" />
              <a href={`tel:${t.footer.phone.replace(/\s/g, "")}`} className="hover:text-[#A57F39]">{t.footer.phone}</a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-[#A57F39] mt-0.5 shrink-0" />
              <a href={`mailto:${t.footer.email}`} className="hover:text-[#A57F39]">{t.footer.email}</a>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-[#A57F39] mt-0.5 shrink-0" />
              <span>
                <div className="text-xs uppercase tracking-widest text-[#A57F39] mb-0.5">{t.contact.officeHours}</div>
                {t.contact.officeHoursValue}
              </span>
            </li>
          </ul>
        </aside>
      </section>
    </PublicLayout>
  );
}

function Field({ label, required, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">{label}{required && " *"}</label>
      <input
        data-testid={testid}
        required={required}
        type={type}
        value={value}
        onChange={onChange}
        className="w-full rounded-lg border border-[#A57F39]/25 focus:border-[#A57F39] focus:outline-none px-3 py-2.5 text-[#0F1A2B]"
      />
    </div>
  );
}
